import React from "react";
import {createPortal} from "react-dom";
import {Button, Stack, Typography} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import {COLORS} from "~/style/colors-ts.ts";
import {HelpComponent} from "~/components/help-component/HelpComponent.tsx";import {PageHeaderSlotContext} from "~/components/main/header/PageHeaderSlotContext.ts";

export type PageHeaderBack = {
    label: string;
    onClick: () => void;
};

type Props = {
    title?: React.ReactNode;
    back?: PageHeaderBack;
    /** Shown right after the title, e.g. a Trial / BETA tag or a status tag */
    tag?: React.ReactNode;
    helpContent?: React.ReactNode;
};

/**
 * Renders the page's back control and title in the app's top bar (Mithra 3.1 header).
 * The page body no longer renders its own h1.
 */
export const PageHeader: React.FC<Props> = ({title, back, tag, helpContent}) => {
    const {slot} = React.useContext(PageHeaderSlotContext);
    if (!slot) return null;

    return createPortal(
        <Stack direction="row" alignItems="center" gap={1} sx={{minWidth: 0}}>
            {back &&
                <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    startIcon={<ArrowBack sx={{fontSize: 16}}/>}
                    onClick={back.onClick}
                    title={back.label}
                    sx={{
                        height: 36,
                        maxWidth: 200,
                        flexShrink: 0,
                        px: 1,
                        borderRadius: '8px',
                        fontSize: 12.5,
                        fontWeight: 400,
                        textTransform: 'none',
                        color: COLORS.text.secondary,
                        '&:hover': {backgroundColor: COLORS.grey[50], color: COLORS.text.primary},
                    }}
                >
                    <Typography component="span" fontSize="inherit" noWrap>{back.label}</Typography>
                </Button>}
            {back && title && <Typography component="span" aria-hidden="true" sx={{color: COLORS.grey[400]}}>/</Typography>}
            {title && <Typography
                component="h1"
                noWrap
                title={typeof title === 'string' ? title : undefined}
                sx={{fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', color: COLORS.text.primary}}
            >
                {title}
            </Typography>}
            {tag && <Stack direction="row" alignItems="center" gap={1} sx={{flexShrink: 0}}>{tag}</Stack>}
            {helpContent && <HelpComponent helpContent={helpContent}/>}
        </Stack>,
        slot,
    );
};
